import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FilterState } from '@/components/CompactFilterBar';

export interface SmartFilterPreset {
  name: string;
  description: string;
  filters: Partial<FilterState>;
  isActive: boolean;
}

interface UserProfileData {
  profileType: 'enceinte' | 'maman' | 'les_deux' | null;
  extendRiskMode: boolean;
}

const fetchUserProfile = async (): Promise<UserProfileData> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return { profileType: null, extendRiskMode: false };
  }

  // Parallel fetch for better performance
  const [profileResult, prefsResult] = await Promise.all([
    supabase
      .from('profiles')
      .select('extend_risk_mode')
      .eq('id', user.id)
      .single(),
    supabase
      .from('user_prefs')
      .select('profile_type')
      .eq('user_id', user.id)
      .single()
  ]);

  return {
    profileType: prefsResult.data?.profile_type ?? null,
    extendRiskMode: profileResult.data?.extend_risk_mode ?? false
  };
};

export const useSmartFilters = () => {
  const [smartPreset, setSmartPreset] = useState<SmartFilterPreset | null>(null);
  const [presetDisabled, setPresetDisabled] = useState(false);
  const queryClient = useQueryClient();

  const { data: userProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: fetchUserProfile,
    staleTime: 5 * 60 * 1000,  // 5 min cache
    gcTime: 30 * 60 * 1000,
  });

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const applySmartPreset = useCallback((profileType: 'enceinte' | 'maman' | 'les_deux', extendedMode: boolean) => {
    if (presetDisabled) return;

    let preset: SmartFilterPreset;

    switch (profileType) {
      case 'enceinte':
        preset = {
          name: 'Profil Enceinte',
          description: extendedMode 
            ? 'Mode étendu activé - Toutes catégories avec tri par gravité'
            : 'Catégories de base (25, 64) uniquement',
          filters: {
            pregnancyOnly: true,
            severity: 'all',
            agents: ['listeria', 'salmonella', 'toxoplasma']
          },
          isActive: true
        };
        break;
        
      case 'maman':
        preset = {
          name: 'Profil Maman', 
          description: 'Focus sur les produits bébé',
          filters: {
            category: 'baby-food',
            pregnancyOnly: false
          },
          isActive: true
        };
        break;
        
      case 'les_deux':
        preset = {
          name: 'Profil Complet',
          description: extendedMode
            ? 'Mode étendu activé - Toutes catégories avec tri par gravité'
            : 'Catégories de base (25, 64) uniquement',
          filters: {
            category: 'all',
            pregnancyOnly: false,
            severity: 'all',
            agents: ['listeria', 'salmonella', 'toxoplasma']
          },
          isActive: true
        };
        break;
        
      default:
        preset = {
          name: 'Aucun preset',
          description: '',
          filters: {},
          isActive: false
        };
    }

    setSmartPreset(preset);
  }, [presetDisabled]);

  // Apply preset when profile data changes
  useEffect(() => {
    if (userProfile?.profileType) {
      applySmartPreset(userProfile.profileType, userProfile.extendRiskMode);
    }
  }, [userProfile, applySmartPreset]);

  const disablePreset = () => {
    setPresetDisabled(true);
    setSmartPreset(null);
  };

  const enablePreset = () => {
    setPresetDisabled(false);
    if (userProfile?.profileType) {
      applySmartPreset(userProfile.profileType, userProfile.extendRiskMode);
    }
  };

  const getOrderByClause = () => {
    if (!smartPreset?.isActive || presetDisabled) {
      return 'severity_score desc, published_at desc';
    }

    switch (userProfile?.profileType) {
      case 'enceinte':
        return 'severity_score desc, published_at desc';
      case 'maman':
        return `(CASE WHEN category_id = '25' THEN severity_score + 2 ELSE severity_score END) desc, published_at desc`;
      case 'les_deux':
      default:
        return 'severity_score desc, published_at desc';
    }
  };

  return {
    userProfile: userProfile?.profileType ?? null,
    smartPreset,
    presetDisabled,
    extendRiskMode: userProfile?.extendRiskMode ?? false,
    disablePreset,
    enablePreset,
    getOrderByClause
  };
};
